import React, { useState } from 'react';
import Layout from './layout/Layout';
import { usePageContent } from '../context/ContentContext';

/**
 * Home page — the approved layout.
 *
 * The logo sits directly on the hero image, with the intro expanding beneath it
 * when clicked. An earlier revision wrapped all of this in a dark translucent
 * panel; that version was rejected, so keep the logo and copy on the image
 * itself rather than inside a container.
 *
 * Text and imagery come from the site content admin, so wording can be changed
 * without a rebuild.
 */
const Home = () => {
    const [showInfo, setShowInfo] = useState(false);
    const { t, g } = usePageContent('home');

    return (
        <Layout>
            <section className="h-screen relative">
                <img
                    src={t('hero.image')}
                    alt={t('hero.imageAlt')}
                    className="w-full h-full object-cover"
                />

                <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30 flex items-center justify-center overflow-y-auto">
                    <div className="text-center text-white py-20 px-4">
                        <div
                            className="inline-block cursor-pointer"
                            onClick={() => setShowInfo(!showInfo)}
                        >
                            <img
                                src={g('header.logo')}
                                alt={g('header.logoAlt')}
                                className="h-24 md:h-32 mb-4 md:mb-6 transition-all duration-300 hover:scale-105 mx-auto"
                            />
                            {!showInfo && (
                                <p className="font-poppins font-light text-sm md:text-body mt-4 opacity-75">
                                    {t('hero.prompt')}
                                </p>
                            )}
                        </div>

                        {showInfo && (
                            <div
                                className="mt-8 max-w-3xl mx-auto px-4 animate-fade-in cursor-pointer"
                                onClick={() => setShowInfo(false)}
                            >
                                {t('intro.paragraphs').map((paragraph, index) => (
                                    <p
                                        key={index}
                                        className="font-poppins font-normal text-sm md:text-body mb-4 md:mb-6"
                                    >
                                        {paragraph}
                                    </p>
                                ))}

                                <p className="font-poppins font-normal text-sm md:text-body mb-4 md:mb-6">
                                    {t('intro.stockistBefore')}
                                    <a
                                        href={t('intro.stockistLinkUrl')}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary-red hover:text-pink-300 ml-1"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {t('intro.stockistLinkText')}
                                    </a> {t('intro.stockistAfter')}
                                </p>

                                <div className="flex flex-wrap justify-center gap-2 md:gap-4 mt-4 md:mt-6">
                                    <a
                                        href={`mailto:${t('intro.emailAddress')}`}
                                        className="text-primary-red hover:text-pink-300 text-sm md:text-base transition-colors"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {t('intro.emailText')}
                                    </a>
                                    <span className="text-gray-400">|</span>
                                    <a
                                        href={t('intro.instagramUrl')}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary-red hover:text-pink-300 text-sm md:text-base transition-colors"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {t('intro.instagramText')}
                                    </a>
                                </div>

                                <p className="font-poppins font-light text-sub mt-6 opacity-75">
                                    {t('intro.closePrompt')}
                                </p>
                            </div>
                        )}

                        {!showInfo && (
                            <>
                                <p className="font-poppins font-light text-xl md:text-h2 mt-6">
                                    {t('hero.tagline')}
                                </p>
                                <p className="font-poppins font-light text-sm md:text-body mt-4">
                                    {t('hero.established')}
                                </p>
                            </>
                        )}
                    </div>
                </div>
            </section>
        </Layout>
    );
};

export default Home;
